import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n';

const SITE_URL = 'https://govipportugal.com';

// Helper: ensure a single <meta> / <link> tag with given selector exists, creating or updating it
const upsertHeadTag = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tagName || 'meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'tagName') return;
    el.setAttribute(k, v);
  });
  return el;
};

// Remove all elements matching selector (for cleanup of dynamically added hreflang tags)
const removeAll = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

export const SEOHead = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const lang = (i18n.language || DEFAULT_LANGUAGE).split('-')[0];

    // Strip language prefix from current path to derive page-relative path
    const segments = location.pathname.split('/').filter(Boolean);
    let pageSegments = segments;
    if (SUPPORTED_LANGUAGES.includes(segments[0])) {
      pageSegments = segments.slice(1);
    }
    const pagePath = pageSegments.length ? '/' + pageSegments.join('/') : '';

    const title = t('seo.title');
    const description = t('seo.description');
    const keywords = t('seo.keywords');

    // <title>
    document.title = title;

    // Standard meta tags
    upsertHeadTag('meta[name="description"]', { name: 'description', content: description });
    upsertHeadTag('meta[name="keywords"]', { name: 'keywords', content: keywords });

    // Open Graph
    upsertHeadTag('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertHeadTag('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertHeadTag('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertHeadTag('meta[property="og:locale"]', {
      property: 'og:locale',
      content: { pt: 'pt_PT', en: 'en_GB', es: 'es_ES', fr: 'fr_FR' }[lang] || 'en_GB',
    });
    upsertHeadTag('meta[property="og:url"]', {
      property: 'og:url',
      content: `${SITE_URL}/${lang}${pagePath}`,
    });
    upsertHeadTag('meta[property="og:site_name"]', { property: 'og:site_name', content: 'GO VIP Portugal' });

    // Twitter Card
    upsertHeadTag('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertHeadTag('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertHeadTag('meta[name="twitter:description"]', { name: 'twitter:description', content: description });

    // Canonical URL
    upsertHeadTag('link[rel="canonical"]', {
      tagName: 'link',
      rel: 'canonical',
      href: `${SITE_URL}/${lang}${pagePath}`,
    });

    // hreflang alternates — clean previous, then add fresh
    removeAll('link[rel="alternate"][data-hreflang]');
    SUPPORTED_LANGUAGES.forEach((code) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', `${SITE_URL}/${code}${pagePath}`);
      link.setAttribute('data-hreflang', '1');
      document.head.appendChild(link);
    });
    // x-default → English
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', `${SITE_URL}/en${pagePath}`);
    xDefault.setAttribute('data-hreflang', '1');
    document.head.appendChild(xDefault);

    // Schema.org JSON-LD: LocalBusiness + TaxiService (homepage only)
    const schemaId = 'govip-schema-jsonld';
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement('script');
      schemaTag.id = schemaId;
      schemaTag.type = 'application/ld+json';
      document.head.appendChild(schemaTag);
    }

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          '@id': `${SITE_URL}/#business`,
          name: 'GO VIP Portugal',
          description,
          url: `${SITE_URL}/${lang}`,
          image: `${SITE_URL}/logo-govip.jpg`,
          telephone: '+351932651940',
          email: 'info@govipportugal.com',
          priceRange: '€€€',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Porto',
            addressCountry: 'PT',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 41.1579,
            longitude: -8.6291,
          },
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '00:00',
            closes: '23:59',
          },
          sameAs: [
            'https://www.facebook.com/people/Go-Vip-Portugal/61571543287074/',
            'https://www.instagram.com/govipportugal/',
            'https://www.linkedin.com/company/go-vip-portugal/',
          ],
          areaServed: [
            { '@type': 'Country', name: 'Portugal' },
            { '@type': 'Country', name: 'Spain' },
          ],
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '5.0',
            reviewCount: '27',
            bestRating: '5',
            worstRating: '1',
          },
        },
        {
          '@type': 'TaxiService',
          '@id': `${SITE_URL}/#taxiservice`,
          name: 'Porto Airport Transfer & Private Tours',
          provider: { '@id': `${SITE_URL}/#business` },
          serviceType: 'Airport Transfer, Private Tour',
          areaServed: [
            { '@type': 'City', name: 'Porto' },
            { '@type': 'City', name: 'Braga' },
            { '@type': 'City', name: 'Guimarães' },
            { '@type': 'City', name: 'Aveiro' },
            { '@type': 'AdministrativeArea', name: 'Douro Valley' },
            { '@type': 'City', name: 'Santiago de Compostela' },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: 'GO VIP Portugal',
          inLanguage: lang,
          publisher: { '@id': `${SITE_URL}/#business` },
        },
      ],
    };
    schemaTag.textContent = JSON.stringify(schema);
  }, [t, i18n.language, location.pathname]);

  return null;
};
